import { PANELS, TAB_LABEL_PANELS, panelGroups } from '../../config/panelRegistry';
import { normalizeLabelInput, toStoredLabel } from './nodeChartLabels';

/** Всё, что редактируется на вкладке: панели разделов плюс названия разделов в меню. */
const EDITABLE = [...TAB_LABEL_PANELS, ...PANELS];

/**
 * Подготовка данных для вкладки «Интерфейс».
 *
 * Модулем, а не внутри компонента, по той же причине, что `nodeChartLabels.js`: компоненты
 * в проекте юнит-тестами не покрываются, а семантика сброса и сборка черновиков проверять надо.
 *
 * Семантика полей переиспользуется у карточки графиков пакета нод (`toStoredLabel`): пустое
 * поле и значение, совпавшее с умолчанием, одинаково означают «сбросить». Хранить копию
 * умолчания незачем — после правки реестра она бы его перебивала.
 */

export { MAX_LABEL_LENGTH, normalizeLabelInput, toStoredLabel } from './nodeChartLabels';

/** Ключи полей ввода. JSON, а не склейка через разделитель — как у графиков нод. */
export const panelFieldKey = {
  title: (panelId) => JSON.stringify(['panel-title', panelId]),
  description: (panelId) => JSON.stringify(['panel-description', panelId])
};

/** Группы вкладки: те же, что у реестра. Отдельная функция — чтобы экран не знал о реестре. */
export const interfacePanelGroups = () => panelGroups();

/**
 * Черновики полей по ответу сервера: ключ поля → введённое значение (пустая строка — не
 * переименовано). Идут все панели реестра, а не только настроенные: у поля должен быть
 * плейсхолдер с умолчанием даже тогда, когда настройки ещё нет.
 */
export const initialPanelLabels = (overrides) => {
  const drafts = {};

  for (const panel of EDITABLE) {
    drafts[panelFieldKey.title(panel.id)] = overrides?.[panel.id]?.title ?? '';
    drafts[panelFieldKey.description(panel.id)] = overrides?.[panel.id]?.description ?? '';
  }

  return drafts;
};

/** Видимость всех панелей реестра: настройка тенанта, иначе умолчание реестра. */
export const initialPanelVisibility = (overrides) => {
  const visibility = {};

  for (const panel of EDITABLE) {
    const stored = overrides?.[panel.id]?.visible;
    visibility[panel.id] = typeof stored === 'boolean' ? stored : panel.hiddenByDefault !== true;
  }

  return visibility;
};

/** Всё редактируемое в группе: название раздела в меню плюс его панели и столбцы таблиц. */
export const groupRows = (group) =>
  [group.tabLabel, ...group.kpi, ...group.cards, ...group.columns].filter(Boolean);

/** Сколько строк вкладки переименовано — для подписи группы. */
export const countRenamedPanels = (group, savedLabels) =>
  groupRows(group).filter(
    (panel) =>
      normalizeLabelInput(savedLabels?.[panelFieldKey.title(panel.id)]) !== null
      || normalizeLabelInput(savedLabels?.[panelFieldKey.description(panel.id)]) !== null
  ).length;

/** Сколько панелей вкладки скрыто — для подписи группы. */
export const countHiddenPanels = (group, visibility) =>
  groupRows(group).filter((panel) => visibility?.[panel.id] === false).length;

/**
 * Что отправить на сервер для одной панели. Обе подписи уходят вместе: эндпоинт принимает их
 * одной записью, и раздельная отправка означала бы две неатомарные записи на одну правку.
 */
export const panelLabelsPayload = (panel, drafts) => ({
  title: toStoredLabel(drafts?.[panelFieldKey.title(panel.id)], panel.title),
  description: toStoredLabel(drafts?.[panelFieldKey.description(panel.id)], panel.description ?? null)
});
